"use client";

import { c as _c } from "react/compiler-runtime";
import { useRef } from "react";
import { useScroll, useTransform } from "motion/react";
interface AntiParallelScrollOptions {
  /**
   * The maximum vertical offset for the animation in pixels.
   * Default: 100
   */
  offset?: number;
  /**
   * Optional manual container ref. If not provided, a ref will be created.
   */
  containerRef?: React.RefObject<HTMLElement | null>;
}

/**
 * Reusable hook to create anti-parallel scrolling offsets (one up, one down).
 *
 * @param options - Configuration options for the scroll animation
 * @returns An object containing the container ref and two motion values (y1, y2).
 */
export function useAntiParallelScroll(t0) {
  const $ = _c(17);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    offset: t2,
    containerRef
  } = t1;
  const offset = t2 === undefined ? 100 : t2;
  const internalRef = useRef(null);
  const targetRef = containerRef || internalRef;
  const t3 = targetRef as React.RefObject<HTMLElement>;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = ["start end", "end start"];
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== t3) {
    t5 = {
      target: t3,
      offset: t4
    };
    $[3] = t3;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const {
    scrollYProgress
  } = useScroll(t5);
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [0, 1];
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const t7 = -offset;
  let t8;
  if ($[6] !== offset || $[7] !== t7) {
    t8 = [offset, t7];
    $[6] = offset;
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const y1 = useTransform(scrollYProgress, t6, t8);
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [0, 1];
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const t10 = -offset;
  let t11;
  if ($[10] !== offset || $[11] !== t10) {
    t11 = [t10, offset];
    $[10] = offset;
    $[11] = t10;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  const y2 = useTransform(scrollYProgress, t9, t11);
  let t12;
  if ($[13] !== targetRef || $[14] !== y1 || $[15] !== y2) {
    t12 = {
      ref: targetRef,
      y1,
      y2
    };
    $[13] = targetRef;
    $[14] = y1;
    $[15] = y2;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  return t12;
}